import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { getLogger } from '@confluence/logger';

import ActiveCommentsQuery, {
	type ActiveCommentsQuery$data,
	type ActiveCommentsQuery$variables,
} from './__generated__/ActiveCommentsQuery.graphql.ts';
import {
	buildConsistentActiveCommentsQueryPreloadVariables,
	buildConsistentActiveCommentsQueryVariables,
	ACTIVE_COMMENTS_QUERY_PRELOAD_LOADING_FLAG,
} from './helper/buildConsistentQueryVariablesHelper.ts';

const logger = getLogger('preloadFooterComments');

export function preloadFooterComments(contentId: string, isArchived?: boolean) {
	// Clear the cache BEFORE load. This query only every gets called with cache-only, so this prevents stale cache errors
	try {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (!process.env.REACT_SSR) {
			// On the server, we can sometimes fail to update the cache either at all, or at least in the expected order, which causes this query to look like it's forever loading
			writeQueryInterop<ActiveCommentsQuery$data, ActiveCommentsQuery$variables>({
				query: ActiveCommentsQuery,
				variables: buildConsistentActiveCommentsQueryPreloadVariables(contentId, isArchived),
				data: {
					comments: {
						nodes: [],
						totalCount: ACTIVE_COMMENTS_QUERY_PRELOAD_LOADING_FLAG, // We can't do cache.evict based on apollo version, so use 	a flag
						__typename: 'PaginatedCommentList',
					},
				},
			});

			// Always write to the main footer cache - ObjectCommentContainer reads from it directly in SPA (rather than using preload vars)
			writeQueryInterop<ActiveCommentsQuery$data, ActiveCommentsQuery$variables>({
				query: ActiveCommentsQuery,
				variables: buildConsistentActiveCommentsQueryVariables({
					pageId: contentId,
					type: 'FOOTER',
					isArchived,
				}),
				data: {
					comments: {
						nodes: [],
						totalCount: ACTIVE_COMMENTS_QUERY_PRELOAD_LOADING_FLAG, // We can't do cache.evict based on apollo version, so use 	a flag
						__typename: 'PaginatedCommentList',
					},
				},
			});
		}
	} catch (error) {
		// Do nothing, this is expected if the query is not in the cache
		logger.error`Error clearing preload cache for footer comments for contentId: ${contentId}, error: ${error}`;
	}
	if (contentId) {
		return query<ActiveCommentsQuery$data, ActiveCommentsQuery$variables>({
			query: ActiveCommentsQuery,
			variables: buildConsistentActiveCommentsQueryPreloadVariables(contentId, isArchived),
			fetchPolicy: 'network-only',
		}).then((response: any) => {
			try {
				// Always write to the main footer cache - ObjectCommentContainer reads from it directly in SPA (rather than using preload vars)
				writeQueryInterop<ActiveCommentsQuery$data, ActiveCommentsQuery$variables>({
					query: ActiveCommentsQuery,
					variables: buildConsistentActiveCommentsQueryVariables({
						pageId: contentId,
						type: 'FOOTER',
						isArchived,
					}),
					data: response.data,
				});
			} catch (error) {
				logger.error`Error writing to full cache for footer comments for contentId: ${contentId}, error: ${error}`;
			}
		});
	} else {
		return Promise.resolve({});
	}
}
